<template>
  <div class="scratch-game-container">
    <!-- Animation de déblocage du badge -->
    <div v-if="showBadgeUnlockAnimation" class="badge-unlock-overlay">
      <div class="badge-unlock-animation">
        <img :src="badgeScratchImage" alt="Badge" class="badge-icon" />
        <h2>Badge débloqué !</h2>
        <h3>{{ badgeData.name }}</h3>
        <p>{{ badgeData.description }}</p>
        <button @click="closeBadgeAnimation" class="close-animation-btn">
          Continuer
        </button>
      </div>
    </div>

    <!-- Intégration du GameGuide -->
    <GameGuide
      v-if="!gameStarted"
      gameId="scratch-coding"
      :forceShow="true"
      @start-game="onGuideComplete"
      @skip-intro="onGuideComplete"
    />

    <!-- Zone de jeu principale -->
    <div class="game-playground" v-if="gameStarted && !showResults">
      <!-- Barre de progression -->
      <div class="progress-container">
        <div class="progress-steps">
          <div class="progress-step" :class="{ completed: currentLevel > 0 }">
            <div class="step-icon">🎮</div>
            <div class="step-label">Démarrage</div>
          </div>

          <div class="progress-connector"></div>

          <div
            class="progress-step"
            :class="{
              completed: currentLevel >= Math.floor(levels.length / 3),
            }"
          >
            <div class="step-icon">🏃</div>
            <div class="step-label">En cours</div>
          </div>

          <div class="progress-connector"></div>

          <div
            class="progress-step"
            :class="{
              completed: currentLevel >= Math.floor((levels.length * 2) / 3),
            }"
          >
            <div class="step-icon">🔍</div>
            <div class="step-label">Avancé</div>
          </div>

          <div class="progress-connector"></div>

          <div
            class="progress-step"
            :class="{ completed: currentLevel >= levels.length }"
          >
            <div class="step-icon">🏆</div>
            <div class="step-label">Terminé</div>
          </div>
        </div>

        <div class="progress-bar">
          <div
            class="progress-fill"
            :style="{ width: progressPercentage + '%' }"
          ></div>
          <div class="progress-text">
            {{ currentLevel }} / {{ levels.length }}
          </div>
        </div>
      </div>

      <div class="level-indicator">
        <div class="level-badge">Niveau {{ currentLevel + 1 }}</div>
        <div class="score-badge">Score: {{ score }}</div>
      </div>

      <!-- Contenu du jeu -->
      <div class="game-content">
        <!-- Objectif en grand et visible -->
        <div class="objective-card">
          <div class="objective-icon">🎯</div>
          <div class="objective-content">
            <div class="objective-title">Ton objectif :</div>
            <div class="objective-text">{{ currentLevelData.objective }}</div>
          </div>
        </div>

        <!-- Actions du jeu -->
        <div class="game-actions">
          <button
            @click="runCode"
            class="action-button run-button"
            :disabled="isRunning"
          >
            <span class="btn-icon">▶️</span>
            <span class="btn-text">{{ isRunning ? 'Exécution...' : 'Exécuter' }}</span>
          </button>
          <button
            @click="resetWorkspace"
            class="action-button reset-button"
          >
            <span class="btn-icon">🔄</span>
            <span class="btn-text">Réinitialiser</span>
          </button>
          <button
            @click="nextLevel"
            class="action-button next-button"
            v-if="levelCompleted"
          >
            <span class="btn-icon">➡️</span>
            <span class="btn-text">Niveau suivant</span>
          </button>
        </div>

        <!-- Feedback -->
        <div class="feedback-message" :class="feedbackClass" v-if="feedback">
          {{ feedback }}
        </div>

        <div class="game-layout">
          <!-- Zone visuelle avec Flamou -->
          <div class="visual-scene">
            <div class="scene-container" :class="{ 'classic-background': isClassicBackground }">
              <SpaceBackground
                v-if="usesSpaceBackground"
                class="space-background-layer"
                :theme="currentBackgroundTheme"
              />
              <div
                v-if="!isClassicBackground"
                class="flamou-spotlight"
                :style="flamouSpotlightStyle"
              ></div>
              <div class="flamou-character" :style="flamouStyle">
                <img :src="currentFlamouImage" alt="Flamou" class="flamou-img" />
                <div v-if="flamouMessage" class="speech-bubble">
                  {{ flamouMessage }}
                </div>
              </div>
            </div>
            <div class="scene-info">
              <div class="info-item">
                <strong>Position:</strong> {{ flamouPosition }}
              </div>
              <div class="info-item" v-if="flamouLastAction">
                <strong>Dernière action:</strong> {{ flamouLastAction }}
              </div>
            </div>
            <div class="background-selector">
              <span class="selector-label">Choisis ton décor :</span>
              <div class="background-buttons">
                <button
                  v-for="option in backgroundOptions"
                  :key="option.value"
                  type="button"
                  class="background-button"
                  :class="{ active: currentBackgroundTheme === option.value }"
                  @click="setBackgroundTheme(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
          </div>

          <!-- Zone Blockly -->
          <div class="blockly-section">
            <div id="blocklyDiv" ref="blocklyDiv" class="blockly-workspace"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Écran des résultats -->
    <div class="results-overlay" v-if="showResults">
      <div class="results-modal">
        <div class="results-header">
          <div class="results-title-container">
            <div class="results-title-icon">🏆</div>
            <h2 class="results-title">Félicitations !</h2>
          </div>
          <p class="results-subtitle">
            Tu as terminé le jeu de Programmation Scratch
          </p>
        </div>

        <div class="results-statistics">
          <div class="stat-item">
            <div class="stat-value">{{ score }}</div>
            <div class="stat-label">Score Total</div>
          </div>

          <div class="stat-item">
            <div class="stat-value">{{ correctAnswers }}</div>
            <div class="stat-label">Niveaux Réussis</div>
          </div>

          <div class="stat-item">
            <div class="stat-value">
              {{ Math.floor((correctAnswers / levels.length) * 100) }}%
            </div>
            <div class="stat-label">Précision</div>
          </div>
        </div>

        <p class="result-message">
          {{ getResultMessage() }}
        </p>

        <div class="results-actions">
          <button @click="restartGame" class="action-button restart-button">
            <span class="btn-icon">🔄</span>
            <span class="btn-text">Rejouer</span>
          </button>
          <button @click="goToNextGame" class="action-button metier-button">
            <span class="btn-icon">🎮</span>
            <span class="btn-text">Prochain jeu</span>
          </button>
          <button @click="retournerAuMenu" class="action-button return-button">
            <span class="btn-icon">🏠</span>
            <span class="btn-text">Retour au menu</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import * as Blockly from 'blockly'
import { javascriptGenerator } from 'blockly/javascript'
import { unlockBadge, isBadgeUnlocked } from '@/utils/badges'
import GameGuide from '@/components/GameGuideComponent.vue'
import AuthService from '@/services/AuthService'
import { useGameTimer } from '@/services/useGameTimer'
import flamouImage from '@/assets/flamou/intresting.png'
import flamouHappy from '@/assets/flamou/happy.png'
import flamouHappy2 from '@/assets/flamou/happy2.png'
import flamouNormal from '@/assets/flamou/normal.png'
import flamouHey from '@/assets/flamou/hey.png'
import badgeScratchImage from '@/assets/badges_skills/Badge_jeu_de_formes.png' // Placeholder - you can change this
import GameNavigationService from '@/services/GameNavigationService'
import SpaceBackground from '@/components/SpaceBackground.vue'

export default {
  name: 'ScratchGame',
  components: {
    GameGuide,
    SpaceBackground,
  },
  setup() {
    const router = useRouter()
    const {
      showGameModal,
      timeRemaining,
      startTimer,
      stopTimer,
      resetTimer,
      getRandomGameRoute,
      closeModal,
    } = useGameTimer()

    // Variables réactives
    const gameStarted = ref(false)
    const currentLevel = ref(0)
    const feedback = ref('')
    const feedbackClass = ref('')
    const levelCompleted = ref(false)
    const isRunning = ref(false)
    const score = ref(0)
    const correctAnswers = ref(0)
    const showResults = ref(false)
    const showBadgeUnlockAnimation = ref(false)
    const badgeScratchId = ref(4) // You'll need to add this badge to your badge system
    const levelResults = ref({})
    const blocklyDiv = ref(null)
    let workspace = null

    // Variables pour Flamou et la scène visuelle
    const flamouPosition = ref(0)
    const flamouMessage = ref('')
    const currentFlamouImage = ref(flamouNormal)
    const flamouLastAction = ref('')
    const flamouActions = ref([]) // Historique des actions pour vérification

    // Décors disponibles (incluant un mode classique sobre par défaut)
    const backgroundOptions = [
      { value: 'classic', label: 'Classique', usesSpaceBackground: false },
      { value: 'cosmic', label: 'Espace', usesSpaceBackground: true },
      { value: 'ocean', label: 'Océan', usesSpaceBackground: true },
      { value: 'cyberpunk', label: 'Cyber', usesSpaceBackground: true },
      { value: 'forest', label: 'Forêt', usesSpaceBackground: true },
      { value: 'snow', label: 'Neige', usesSpaceBackground: true },
    ]

    const backgroundValues = backgroundOptions.map((option) => option.value)
    const currentBackgroundTheme = ref(backgroundOptions[0].value)

    const setBackgroundTheme = (theme) => {
      if (backgroundValues.includes(theme)) {
        currentBackgroundTheme.value = theme
      }
    }

    const usesSpaceBackground = computed(() => {
      const option = backgroundOptions.find(
        (opt) => opt.value === currentBackgroundTheme.value,
      )
      return option ? option.usesSpaceBackground : true
    })

    const isClassicBackground = computed(
      () => currentBackgroundTheme.value === 'classic',
    )

    // Données pour le badge
    const badgeData = ref({
      name: 'Expert en Programmation',
      description:
        'Tu as maîtrisé les bases de la programmation par blocs !',
    })

    // Configuration des niveaux
    const levels = [
      {
        title: 'Niveau 1: Premiers Mots',
        description: 'Apprends à faire parler Flamou',
        objective: 'Fais dire "Bonjour" à Flamou',
        expectedActions: [{ type: 'say', value: 'Bonjour' }],
        toolbox: 'flamou_basic',
      },
      {
        title: 'Niveau 2: Premiers Pas',
        description: 'Fais bouger Flamou',
        objective: 'Fais avancer Flamou de 3 pas',
        expectedActions: [{ type: 'move', value: 3 }],
        toolbox: 'flamou_move',
      },
      {
        title: 'Niveau 3: Séquence Simple',
        description: 'Combine plusieurs actions',
        objective: 'Fais dire "Salut" à Flamou puis fais-le avancer de 2 pas',
        expectedActions: [
          { type: 'say', value: 'Salut' },
          { type: 'move', value: 2 },
        ],
        toolbox: 'flamou_combo',
      },
      {
        title: 'Niveau 4: Danse de Flamou',
        description: 'Crée une petite chorégraphie',
        objective:
          'Fais avancer Flamou de 1 pas, puis fais-le tourner, puis fais-le avancer de 1 pas',
        expectedActions: [
          { type: 'move', value: 1 },
          { type: 'turn' },
          { type: 'move', value: 1 },
        ],
        toolbox: 'flamou_combo',
      },
      {
        title: 'Niveau 5: Boucle',
        description: 'Répète des actions',
        objective: 'Fais dire "OK" à Flamou 3 fois en utilisant une boucle',
        expectedActions: [
          { type: 'say', value: 'OK' },
          { type: 'say', value: 'OK' },
          { type: 'say', value: 'OK' },
        ],
        toolbox: 'flamou_loops',
      },
    ]

    const currentLevelData = computed(() => levels[currentLevel.value] || {})

    // Calcul du pourcentage de progression
    const progressPercentage = computed(() => {
      return (currentLevel.value / levels.length) * 100
    })

    // Style de Flamou (pour animer la position)
    const flamouStyle = computed(() => {
      return {
        left: `${50 + flamouPosition.value * 60}px`,
        transition: 'left 0.5s ease-in-out',
      }
    })

    const flamouSpotlightStyle = computed(() => {
      const spotlightLeft = 50 + flamouPosition.value * 60
      return {
        left: `${spotlightLeft - 40}px`,
      }
    })

    // Fonctions d'animation de Flamou
    const resetFlamou = () => {
      flamouPosition.value = 0
      flamouMessage.value = ''
      flamouLastAction.value = ''
      flamouActions.value = []
      currentFlamouImage.value = flamouNormal
    }

    const flamouSay = async (message) => {
      currentFlamouImage.value = flamouHey
      flamouMessage.value = message
      flamouActions.value.push({ type: 'say', value: message })
      flamouLastAction.value = `Dit: "${message}"`

      // Garder le message affiché pendant 1.5 secondes
      await new Promise((resolve) => setTimeout(resolve, 1500))
      flamouMessage.value = ''
      currentFlamouImage.value = flamouNormal
    }

    const flamouMove = async (steps) => {
      currentFlamouImage.value = flamouHappy
      flamouPosition.value += steps
      flamouActions.value.push({ type: 'move', value: steps })
      flamouLastAction.value = `Avance de ${steps} pas`

      // Temps d'animation
      await new Promise((resolve) => setTimeout(resolve, 500))
      currentFlamouImage.value = flamouNormal
    }

    const flamouTurn = async () => {
      flamouActions.value.push({ type: 'turn' })
      flamouLastAction.value = 'Fait un tour'
      currentFlamouImage.value = flamouHappy2

      await new Promise((resolve) => setTimeout(resolve, 500))
      currentFlamouImage.value = flamouNormal
    }

    // Définir les blocs personnalisés pour Flamou
    Blockly.Blocks['flamou_say'] = {
      init: function () {
        this.appendValueInput('MESSAGE')
          .setCheck('String')
          .appendField('Flamou dit')
        this.setPreviousStatement(true, null)
        this.setNextStatement(true, null)
        this.setColour(160)
        this.setTooltip('Fait dire quelque chose à Flamou')
        this.setHelpUrl('')
      },
    }

    Blockly.Blocks['flamou_move'] = {
      init: function () {
        this.appendValueInput('STEPS')
          .setCheck('Number')
          .appendField('Flamou avance de')
        this.appendDummyInput().appendField('pas')
        this.setPreviousStatement(true, null)
        this.setNextStatement(true, null)
        this.setColour(230)
        this.setTooltip('Fait avancer Flamou')
        this.setHelpUrl('')
      },
    }

    Blockly.Blocks['flamou_turn'] = {
      init: function () {
        this.appendDummyInput().appendField('Flamou fait un tour')
        this.setPreviousStatement(true, null)
        this.setNextStatement(true, null)
        this.setColour(290)
        this.setTooltip('Fait tourner Flamou')
        this.setHelpUrl('')
      },
    }

    // Générateurs JavaScript pour les blocs personnalisés
    javascriptGenerator.forBlock['flamou_say'] = function (block) {
      const msg =
        javascriptGenerator.valueToCode(
          block,
          'MESSAGE',
          javascriptGenerator.ORDER_NONE,
        ) || "''"
      const code = 'await window.flamouSay(' + msg + ');\n'
      return code
    }

    javascriptGenerator.forBlock['flamou_move'] = function (block) {
      const steps =
        javascriptGenerator.valueToCode(
          block,
          'STEPS',
          javascriptGenerator.ORDER_ATOMIC,
        ) || '0'
      const code = 'await window.flamouMove(' + steps + ');\n'
      return code
    }

    javascriptGenerator.forBlock['flamou_turn'] = function () {
      return 'await window.flamouTurn();\n'
    }

    // Définir un générateur personnalisé pour text_print (une seule fois)
    javascriptGenerator.forBlock['text_print'] = function (block) {
      const msg = javascriptGenerator.valueToCode(
        block,
        'TEXT',
        javascriptGenerator.ORDER_NONE,
      ) || "''"
      const code = 'window.gameOutput(' + msg + ');\n'
      return code
    }

    // Initialiser Blockly workspace
    const initBlockly = async () => {
      await nextTick()
      if (!blocklyDiv.value) return

      // Ne créer le workspace que s'il n'existe pas déjà
      if (workspace) return

      const toolbox = getToolboxForLevel(currentLevelData.value.toolbox)

      workspace = Blockly.inject(blocklyDiv.value, {
        toolbox: toolbox,
        scrollbars: true,
        trashcan: true,
        zoom: {
          controls: true,
          wheel: true,
          startScale: 1.0,
          maxScale: 3,
          minScale: 0.3,
          scaleSpeed: 1.2,
        },
        grid: {
          spacing: 20,
          length: 3,
          colour: '#ccc',
          snap: true,
        },
      })
    }

    // Obtenir la boîte à outils pour le niveau
    const getToolboxForLevel = (toolboxType) => {
      const flamouBasicToolbox = {
        kind: 'flyoutToolbox',
        contents: [
          {
            kind: 'block',
            type: 'flamou_say',
            inputs: {
              MESSAGE: {
                shadow: {
                  type: 'text',
                  fields: {
                    TEXT: 'Bonjour',
                  },
                },
              },
            },
          },
        ],
      }

      const flamouMoveToolbox = {
        kind: 'flyoutToolbox',
        contents: [
          {
            kind: 'block',
            type: 'flamou_move',
            inputs: {
              STEPS: {
                shadow: {
                  type: 'math_number',
                  fields: {
                    NUM: 3,
                  },
                },
              },
            },
          },
        ],
      }

      const flamouComboToolbox = {
        kind: 'flyoutToolbox',
        contents: [
          {
            kind: 'block',
            type: 'flamou_say',
            inputs: {
              MESSAGE: {
                shadow: {
                  type: 'text',
                  fields: {
                    TEXT: 'Salut',
                  },
                },
              },
            },
          },
          {
            kind: 'block',
            type: 'flamou_move',
            inputs: {
              STEPS: {
                shadow: {
                  type: 'math_number',
                  fields: {
                    NUM: 2,
                  },
                },
              },
            },
          },
          {
            kind: 'block',
            type: 'flamou_turn',
          },
        ],
      }

      const flamouLoopsToolbox = {
        kind: 'flyoutToolbox',
        contents: [
          {
            kind: 'block',
            type: 'controls_repeat_ext',
            inputs: {
              TIMES: {
                shadow: {
                  type: 'math_number',
                  fields: {
                    NUM: 3,
                  },
                },
              },
            },
          },
          {
            kind: 'block',
            type: 'flamou_say',
            inputs: {
              MESSAGE: {
                shadow: {
                  type: 'text',
                  fields: {
                    TEXT: 'OK',
                  },
                },
              },
            },
          },
        ],
      }

      const basicToolbox = {
        kind: 'categoryToolbox',
        contents: [
          {
            kind: 'category',
            name: 'Texte',
            contents: [
              {
                kind: 'block',
                type: 'text_print',
                inputs: {
                  TEXT: {
                    shadow: {
                      type: 'text',
                      fields: {
                        TEXT: 'Bonjour',
                      },
                    },
                  },
                },
              },
              {
                kind: 'block',
                type: 'text',
                fields: {
                  TEXT: 'Bonjour',
                },
              },
            ],
          },
        ],
      }

      const mathToolbox = {
        kind: 'categoryToolbox',
        contents: [
          {
            kind: 'category',
            name: 'Texte',
            contents: [
              {
                kind: 'block',
                type: 'text_print',
              },
              {
                kind: 'block',
                type: 'text',
              },
            ],
          },
          {
            kind: 'category',
            name: 'Math',
            contents: [
              {
                kind: 'block',
                type: 'math_number',
              },
              {
                kind: 'block',
                type: 'math_arithmetic',
              },
            ],
          },
        ],
      }

      const variablesToolbox = {
        kind: 'categoryToolbox',
        contents: [
          {
            kind: 'category',
            name: 'Variables',
            custom: 'VARIABLE',
          },
          {
            kind: 'category',
            name: 'Texte',
            contents: [
              {
                kind: 'block',
                type: 'text_print',
              },
            ],
          },
          {
            kind: 'category',
            name: 'Math',
            contents: [
              {
                kind: 'block',
                type: 'math_number',
              },
            ],
          },
        ],
      }

      const loopsToolbox = {
        kind: 'categoryToolbox',
        contents: [
          {
            kind: 'category',
            name: 'Boucles',
            contents: [
              {
                kind: 'block',
                type: 'controls_repeat_ext',
                inputs: {
                  TIMES: {
                    shadow: {
                      type: 'math_number',
                      fields: {
                        NUM: 3,
                      },
                    },
                  },
                },
              },
            ],
          },
          {
            kind: 'category',
            name: 'Texte',
            contents: [
              {
                kind: 'block',
                type: 'text_print',
              },
              {
                kind: 'block',
                type: 'text',
                fields: {
                  TEXT: 'OK',
                },
              },
            ],
          },
        ],
      }

      const toolboxes = {
        flamou_basic: flamouBasicToolbox,
        flamou_move: flamouMoveToolbox,
        flamou_combo: flamouComboToolbox,
        flamou_loops: flamouLoopsToolbox,
        basic: basicToolbox,
        math: mathToolbox,
        variables: variablesToolbox,
        loops: loopsToolbox,
      }

      return toolboxes[toolboxType] || flamouBasicToolbox
    }

    // Exécuter le code
    const runCode = async () => {
      isRunning.value = true
      feedback.value = ''
      feedbackClass.value = ''

      // Réinitialiser Flamou
      resetFlamou()

      try {
        // Générer le code JavaScript depuis les blocs
        const code = javascriptGenerator.workspaceToCode(workspace)

        // Exposer les fonctions de Flamou pour le code généré
        window.flamouSay = flamouSay
        window.flamouMove = flamouMove
        window.flamouTurn = flamouTurn

        // Capturer la sortie avec une fonction personnalisée (pour compatibilité)
        const outputLines = []
        window.gameOutput = function (text) {
          outputLines.push(String(text))
        }

        // Exécuter le code (avec async/await)
        try {
          // Utiliser une fonction async pour exécuter le code
          const asyncFunction = new Function(
            'return (async () => { ' + code + ' })()',
          )
          await asyncFunction()
        } catch (error) {
          delete window.gameOutput
          delete window.flamouSay
          delete window.flamouMove
          delete window.flamouTurn
          throw error
        }

        // Nettoyer
        delete window.gameOutput
        delete window.flamouSay
        delete window.flamouMove
        delete window.flamouTurn

        // Vérifier si les actions correspondent à l'objectif
        const expectedActions = currentLevelData.value.expectedActions
        if (expectedActions) {
          // Vérifier si les actions de Flamou correspondent
          const actionsMatch = compareActions(
            flamouActions.value,
            expectedActions,
          )

          if (actionsMatch) {
            feedback.value = 'Bravo ! Tu as réussi ce niveau !'
            feedbackClass.value = 'feedback-correct'
            levelCompleted.value = true
            score.value += 20
            correctAnswers.value++
            saveResultToBackend(currentLevel.value + 1, true)
          } else {
            feedback.value = `Pas tout à fait... Vérifie que tu as fait toutes les actions demandées !`
            feedbackClass.value = 'feedback-incorrect'
            score.value = Math.max(0, score.value - 5)
            saveResultToBackend(currentLevel.value + 1, false)
          }
        }
      } catch (error) {
        feedback.value = `Erreur: ${error.message}`
        feedbackClass.value = 'feedback-incorrect'
      } finally {
        isRunning.value = false
      }
    }

    // Comparer les actions effectuées avec les actions attendues
    const compareActions = (actual, expected) => {
      if (actual.length !== expected.length) return false

      for (let i = 0; i < expected.length; i++) {
        const act = actual[i]
        const exp = expected[i]

        if (act.type !== exp.type) return false

        if (exp.value !== undefined && act.value !== exp.value) return false
      }

      return true
    }

    // Réinitialiser le workspace
    const resetWorkspace = () => {
      if (workspace) {
        workspace.clear()
      }
      feedback.value = ''
      feedbackClass.value = ''
      levelCompleted.value = false
      resetFlamou()
    }

    // Passer au niveau suivant
    const nextLevel = () => {
      currentLevel.value++
      levelCompleted.value = false
      feedback.value = ''
      feedbackClass.value = ''

      if (currentLevel.value >= levels.length) {
        endGame()
      } else {
        loadLevel()
      }
    }

    // Charger un niveau
    const loadLevel = async () => {
      // Réinitialiser Flamou
      resetFlamou()

      if (workspace) {
        // Au lieu de disposer et recréer, on vide et met à jour la toolbox
        workspace.clear()
        const toolbox = getToolboxForLevel(currentLevelData.value.toolbox)
        workspace.updateToolbox(toolbox)
      } else {
        // Si pas de workspace, on en crée un
        await initBlockly()
      }
    }

    // Sauvegarder les résultats
    const saveResultToBackend = (levelNumber, isCorrect) => {
      AuthService.request('get', '/games/scratch-coding')
        .then((response) => {
          let currentData =
            response.data && typeof response.data === 'object'
              ? response.data
              : { currentLevel: 0, completion: 0, levelResults: {} }

          if (!currentData.levelResults) {
            currentData.levelResults = {}
          }

          if (
            typeof currentData.currentLevel !== 'number' ||
            isNaN(currentData.currentLevel)
          ) {
            currentData.currentLevel = 0
          }

          if (currentLevel.value + 1 > currentData.currentLevel) {
            currentData.currentLevel = currentLevel.value + 1
          }

          currentData.levelResults[levelNumber] = isCorrect

          const totalLevels = levels.length
          const completionDecimal =
            totalLevels > 0 ? currentData.currentLevel / totalLevels : 0

          currentData.completion = isNaN(completionDecimal)
            ? 0
            : parseFloat(completionDecimal.toFixed(4))

          return AuthService.request(
            'post',
            '/games/scratch-coding',
            currentData,
          )
        })
        .catch((error) => {
          console.error('Erreur lors de la mise à jour des résultats:', error)
          if (
            error.response &&
            (error.response.status === 401 || error.response.status === 403)
          ) {
            saveResultLocally(levelNumber, isCorrect)
          }
        })
    }

    const saveResultLocally = (levelNumber, isCorrect) => {
      let localData = JSON.parse(
        localStorage.getItem('scratchCodingResults') ||
          '{"currentLevel":0,"completion":0,"levelResults":{}}',
      )

      if (!localData.levelResults) {
        localData.levelResults = {}
      }

      if (
        typeof localData.currentLevel !== 'number' ||
        isNaN(localData.currentLevel)
      ) {
        localData.currentLevel = 0
      }

      if (currentLevel.value + 1 > localData.currentLevel) {
        localData.currentLevel = currentLevel.value + 1
      }

      localData.levelResults[levelNumber] = isCorrect

      const totalLevels = levels.length
      const completionDecimal =
        totalLevels > 0 ? localData.currentLevel / totalLevels : 0

      localData.completion = isNaN(completionDecimal)
        ? 0
        : parseFloat(completionDecimal.toFixed(4))

      localStorage.setItem('scratchCodingResults', JSON.stringify(localData))
    }

    const loadSavedResults = () => {
      AuthService.request('get', '/games/scratch-coding')
        .then((response) => {
          if (response.data && response.data.currentLevel > 0) {
            const savedData = response.data

            if (savedData.currentLevel < levels.length) {
              currentLevel.value = savedData.currentLevel
            }

            levelResults.value = savedData.levelResults || {}

            correctAnswers.value = Object.values(levelResults.value).filter(
              (result) => result === true,
            ).length

            const correctCount = correctAnswers.value
            const incorrectCount =
              Object.keys(levelResults.value).length - correctCount
            score.value = correctCount * 20 - incorrectCount * 5
          }
        })
        .catch(() => {
          const localData = JSON.parse(
            localStorage.getItem('scratchCodingResults') || 'null',
          )

          if (localData && localData.currentLevel > 0) {
            if (localData.currentLevel < levels.length) {
              currentLevel.value = localData.currentLevel
            }

            levelResults.value = localData.levelResults || {}

            correctAnswers.value = Object.values(levelResults.value).filter(
              (result) => result === true,
            ).length

            const correctCount = correctAnswers.value
            const incorrectCount =
              Object.keys(levelResults.value).length - correctCount
            score.value = correctCount * 20 - incorrectCount * 5
          }
        })
    }

    // Méthode pour démarrage du jeu après le guide
    const onGuideComplete = async () => {
      gameStarted.value = true
      loadSavedResults()
      await nextTick()
      await initBlockly()
    }

    // Redémarrer le jeu
    const restartGame = () => {
      currentLevel.value = 0
      score.value = 0
      correctAnswers.value = 0
      showResults.value = false
      levelResults.value = {}
      levelCompleted.value = false

      const resetData = {
        currentLevel: 0,
        completion: 0,
        levelResults: {},
      }

      AuthService.request('post', '/games/scratch-coding', resetData).catch(
        (error) => {
          console.error(
            'Erreur lors de la réinitialisation des données:',
            error,
          )
          localStorage.setItem(
            'scratchCodingResults',
            JSON.stringify(resetData),
          )
        },
      )

      loadLevel()
    }

    // Terminer le jeu
    const endGame = () => {
      showResults.value = true

      if (correctAnswers.value / levels.length >= 0.8) {
        unlockCompletionBadge()
      }
    }

    // Débloquer le badge de complétion
    const unlockCompletionBadge = async () => {
      try {
        const isUnlocked = await isBadgeUnlocked(badgeScratchId.value)
        if (!isUnlocked) {
          const badgeUnlocked = await unlockBadge(badgeScratchId.value)
          if (badgeUnlocked) {
            setTimeout(() => {
              showBadgeUnlockAnimation.value = true
            }, 500)
          }
        }
      } catch (error) {
        console.error('Erreur lors du déblocage du badge Scratch:', error)
      }
    }

    // Fermer l'animation du badge
    const closeBadgeAnimation = () => {
      showBadgeUnlockAnimation.value = false
    }

    // Aller au jeu suivant
    const goToNextGame = () => {
      GameNavigationService.navigateToNextGame(router, '/games/scratch-coding')
    }

    const retournerAuMenu = () => {
      GameNavigationService.returnToDashboard(router)
    }

    // Obtenir un message de résultat personnalisé
    const getResultMessage = () => {
      const accuracy = correctAnswers.value / levels.length

      if (accuracy >= 0.9) {
        return 'Incroyable ! Tu es un vrai programmeur ! Ton logique et ta créativité sont exceptionnelles !'
      } else if (accuracy >= 0.7) {
        return 'Très bien ! Tu comprends bien les concepts de programmation. Continue à pratiquer !'
      } else if (accuracy >= 0.5) {
        return 'Pas mal ! Tu as saisi les bases, mais tu peux encore progresser avec plus de pratique.'
      } else {
        return 'C\'est un bon début ! La programmation demande de la pratique, continue à t\'entraîner !'
      }
    }

    // Lifecycle hooks
    onMounted(() => {
      // Blockly will be initialized when game starts
    })

    onBeforeUnmount(() => {
      if (workspace) {
        try {
          // Désactiver les événements avant de disposer
          workspace.setVisible(false)
          // Attendre un peu avant de disposer
          setTimeout(() => {
            if (workspace) {
              workspace.dispose()
              workspace = null
            }
          }, 100)
        } catch (e) {
          console.error('Erreur lors du nettoyage de Blockly:', e)
        }
      }
    })

    return {
      // État du jeu
      gameStarted,
      currentLevel,
      currentLevelData,
      feedback,
      feedbackClass,
      levelCompleted,
      isRunning,
      score,
      correctAnswers,
      showResults,
      levels,
      progressPercentage,
      levelResults,
      blocklyDiv,

      // Animation du badge
      showBadgeUnlockAnimation,
      badgeData,
      badgeScratchId,

      // Flamou
      flamouPosition,
      flamouMessage,
      currentFlamouImage,
      flamouLastAction,
      flamouStyle,
      flamouSpotlightStyle,
      currentBackgroundTheme,
      backgroundOptions,
      usesSpaceBackground,
      isClassicBackground,
      setBackgroundTheme,

      // Méthodes
      onGuideComplete,
      runCode,
      resetWorkspace,
      nextLevel,
      restartGame,
      retournerAuMenu,
      closeBadgeAnimation,
      goToNextGame,
      getResultMessage,

      showGameModal,
      timeRemaining,
      startTimer,
      stopTimer,
      resetTimer,
      getRandomGameRoute,
      closeModal,
      flamouImage,
      badgeScratchImage,
    }
  },
}
</script>

<style scoped>
/* Base Styles */
.scratch-game-container {
  font-family: 'Comic Sans MS', 'Chalkboard SE', 'Marker Felt', sans-serif;
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  color: #333;
  min-height: 100vh;
}

/* Game Layout */
.game-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 20px;
}

/* Visual Scene */
.visual-scene {
  background-color: #f0f8ff;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

.scene-container {
  flex: 1;
  background: linear-gradient(to bottom, #e3f2fd 0%, #ffffff 100%);
  border-radius: 10px;
  border: 3px solid #9c27b0;
  position: relative;
  overflow: hidden;
  min-height: 300px;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05);
}

.scene-container.classic-background {
  background: linear-gradient(140deg, #f4f4f6 0%, #dfe1e7 55%, #c7cbd4 100%);
  border-color: #bbbfd0;
  box-shadow: inset 0 2px 12px rgba(0, 0, 0, 0.12);
}

.space-background-layer {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

.flamou-spotlight {
  position: absolute;
  bottom: 5px;
  width: 160px;
  height: 160px;
  background: radial-gradient(circle, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.55) 45%, rgba(0, 0, 0, 0.05) 80%);
  filter: blur(14px);
  opacity: 0.95;
  z-index: 6;
  pointer-events: none;
  transition: left 0.5s ease-in-out, opacity 0.3s ease-in-out;
}

.scene-container::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(
    to right,
    #9c27b0,
    #e91e63,
    #9c27b0
  );
  box-shadow: 0 -2px 4px rgba(156, 39, 176, 0.3);
}

.flamou-character {
  position: absolute;
  bottom: 30px;
  left: 50px;
  transition: left 0.5s ease-in-out;
  z-index: 10;
}

.flamou-img {
  width: 80px;
  height: 80px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.speech-bubble {
  position: absolute;
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%);
  background-color: white;
  border: 2px solid #9c27b0;
  border-radius: 15px;
  padding: 10px 15px;
  min-width: 120px;
  max-width: 200px;
  text-align: center;
  font-weight: bold;
  color: #333;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: bounceIn 0.3s ease-out;
}

.speech-bubble::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid #9c27b0;
}

.scene-info {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.background-selector {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selector-label {
  font-size: 0.9rem;
  color: #5a3472;
  font-weight: 600;
}

.background-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.background-button {
  border: 1px solid rgba(156, 39, 176, 0.4);
  background: rgba(255, 255, 255, 0.25);
  color: #7b1fa2;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.background-button:hover {
  background: rgba(233, 30, 99, 0.15);
}

.background-button.active {
  background: linear-gradient(135deg, #9c27b0, #e91e63);
  color: white;
  border-color: transparent;
  box-shadow: 0 3px 10px rgba(233, 30, 99, 0.3);
}

.info-item {
  background-color: rgba(156, 39, 176, 0.1);
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.9rem;
}

.info-item strong {
  color: #9c27b0;
}

/* Blockly Section in Grid */
.game-layout .blockly-section {
  min-height: 400px;
}

/* Animation du badge débloqué */
.badge-unlock-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  animation: fadeIn 0.5s ease-out;
}

.badge-unlock-animation {
  background-color: #fff;
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 0 30px rgba(156, 39, 176, 0.6);
  animation: scaleIn 0.5s ease-out;
}

.badge-icon {
  width: 150px;
  height: 150px;
  margin: 0 auto 20px auto;
  display: block;
  animation: pulse 2s infinite;
}

.badge-unlock-animation h2 {
  color: #9c27b0;
  font-size: 2rem;
  margin-bottom: 10px;
}

.badge-unlock-animation h3 {
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 15px;
}

.badge-unlock-animation p {
  color: #666;
  margin-bottom: 20px;
}

.close-animation-btn {
  background-color: #9c27b0;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 50px;
  font-weight: bold;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-animation-btn:hover {
  background-color: #7b1fa2;
  transform: scale(1.05);
}

/* Responsive pour l'animation du badge */
@media (max-width: 768px) {
  .badge-unlock-animation {
    padding: 25px;
    max-width: 85%;
    margin: 0 15px;
  }

  .badge-icon {
    width: 120px;
    height: 120px;
  }

  .badge-unlock-animation h2 {
    font-size: 1.75rem;
  }

  .badge-unlock-animation h3 {
    font-size: 1.3rem;
  }

  .badge-unlock-animation p {
    font-size: 1rem;
  }

  .close-animation-btn {
    padding: 10px 20px;
    font-size: 1rem;
  }
}

@media (max-width: 576px) {
  .badge-unlock-animation {
    padding: 20px 15px;
    max-width: 90%;
    margin: 0 10px;
  }

  .badge-icon {
    width: 100px;
    height: 100px;
  }

  .badge-unlock-animation h2 {
    font-size: 1.5rem;
  }

  .badge-unlock-animation h3 {
    font-size: 1.2rem;
  }

  .badge-unlock-animation p {
    font-size: 0.95rem;
  }

  .close-animation-btn {
    padding: 10px 18px;
    font-size: 0.95rem;
  }
}

@media (max-width: 400px) {
  .badge-unlock-animation {
    padding: 15px 10px;
    max-width: 95%;
  }

  .badge-icon {
    width: 80px;
    height: 80px;
  }

  .badge-unlock-animation h2 {
    font-size: 1.3rem;
  }

  .badge-unlock-animation h3 {
    font-size: 1.1rem;
  }

  .badge-unlock-animation p {
    font-size: 0.9rem;
  }

  .close-animation-btn {
    padding: 8px 16px;
    font-size: 0.9rem;
  }
}

/* Game Playground */
.game-playground {
  margin-top: 30px;
}

/* Progress Container */
.progress-container {
  margin-bottom: 30px;
}

.progress-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
}

.step-icon {
  width: 40px;
  height: 40px;
  background-color: #e9ecef;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin-bottom: 5px;
  border: 2px solid #ced4da;
  position: relative;
}

.step-label {
  font-size: 0.8rem;
  color: #6c757d;
}

.progress-step.completed .step-icon {
  background-color: #9c27b0;
  color: white;
  border-color: #9c27b0;
}

.progress-step.completed .step-label {
  color: #9c27b0;
  font-weight: bold;
}

.progress-connector {
  height: 2px;
  background-color: #ced4da;
  flex-grow: 1;
  margin: 0 -5px;
  position: relative;
  top: -22px;
  z-index: 1;
}

.progress-bar {
  width: 100%;
  height: 16px;
  background-color: #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #9c27b0, #e91e63);
  border-radius: 8px;
  transition: width 0.5s ease;
}

.progress-text {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  font-weight: bold;
  font-size: 0.75rem;
}

/* Level Indicator */
.level-indicator {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.level-badge,
.score-badge {
  background-color: #9c27b0;
  color: white;
  padding: 8px 16px;
  border-radius: 50px;
  font-weight: bold;
  font-size: 1rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.score-badge {
  background-color: #ff9800;
}

/* Game Content */
.game-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 30px 0;
  background-color: white;
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

/* Objective Card - Plus visible */
.objective-card {
  background: linear-gradient(135deg, #9c27b0 0%, #e91e63 100%);
  border-radius: 15px;
  padding: 20px;
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 6px 20px rgba(156, 39, 176, 0.3);
}

.objective-icon {
  font-size: 3rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.objective-content {
  flex: 1;
  color: white;
}

.objective-title {
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.9;
}

.objective-text {
  font-size: 1.4rem;
  font-weight: bold;
  line-height: 1.4;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Blockly Section */
.blockly-section {
  min-height: 400px;
  background-color: #f9f9f9;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.blockly-workspace {
  width: 100%;
  height: 500px;
}

/* Feedback Message */
.feedback-message {
  font-size: 1.2rem;
  font-weight: bold;
  margin: 0 0 20px 0;
  padding: 15px;
  border-radius: 10px;
  animation: fadeIn 0.5s ease;
  text-align: center;
}

.feedback-correct {
  color: #4caf50;
  background-color: #e8f5e9;
  border: 2px solid #4caf50;
}

.feedback-incorrect {
  color: #f44336;
  background-color: #ffebee;
  border: 2px solid #f44336;
}

/* Game Actions */
.game-actions {
  margin: 10px 0 20px 0;
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
}

.action-button {
  padding: 12px 25px;
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.action-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.run-button {
  background-color: #4caf50;
  color: white;
}

.run-button:hover:not(:disabled) {
  background-color: #388e3c;
  transform: translateY(-2px);
}

.reset-button {
  background-color: #ff9800;
  color: white;
}

.reset-button:hover {
  background-color: #f57c00;
  transform: translateY(-2px);
}

.next-button {
  background-color: #2196f3;
  color: white;
}

.next-button:hover {
  background-color: #1976d2;
  transform: translateY(-2px);
}

.return-button {
  background-color: #6c757d;
}

.return-button:hover {
  background-color: #5a6268;
}

/* Results Modal */
.results-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.results-modal {
  background-color: white;
  border-radius: 20px;
  padding: 30px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.5s ease;
}

.results-header {
  text-align: center;
  margin-bottom: 30px;
}

.results-title-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}

.results-title-icon {
  font-size: 2.5rem;
  margin-right: 15px;
  color: #ff9800;
}

.results-title {
  font-size: 2.2rem;
  color: #9c27b0;
  margin: 0;
}

.results-subtitle {
  font-size: 1.2rem;
  color: #666;
  margin: 0;
}

.results-statistics {
  display: flex;
  justify-content: space-around;
  margin: 30px 0;
}

.stat-item {
  text-align: center;
  background-color: #f5f5f5;
  border-radius: 15px;
  padding: 15px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  width: 28%;
}

.stat-value {
  font-size: 1.8rem;
  font-weight: bold;
  color: #9c27b0;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
}

.result-message {
  text-align: center;
  font-size: 1.1rem;
  color: #555;
  margin: 25px 0;
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 10px;
  line-height: 1.5;
}

.results-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
  flex-wrap: wrap;
}

.restart-button {
  background-color: #9c27b0;
  color: white;
}

.restart-button:hover {
  background-color: #7b1fa2;
}

.metier-button {
  background-color: #4caf50;
  color: white;
}

.metier-button:hover {
  background-color: #388e3c;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes bounceIn {
  0% {
    transform: translateX(-50%) scale(0.3);
    opacity: 0;
  }
  50% {
    transform: translateX(-50%) scale(1.05);
  }
  70% {
    transform: translateX(-50%) scale(0.9);
  }
  100% {
    transform: translateX(-50%) scale(1);
    opacity: 1;
  }
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* Responsive Styles */
@media (max-width: 768px) {
  .game-layout {
    grid-template-columns: 1fr;
  }

  .visual-scene {
    order: -1;
    min-height: 250px;
  }

  .blockly-workspace {
    height: 400px;
  }

  .objective-card {
    flex-direction: column;
    text-align: center;
    padding: 15px;
  }

  .objective-icon {
    font-size: 2.5rem;
  }

  .objective-text {
    font-size: 1.2rem;
  }

  .results-statistics {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }

  .stat-item {
    width: 80%;
  }

  .game-actions {
    flex-direction: column;
    align-items: center;
  }

  .action-button {
    width: 100%;
    max-width: 300px;
  }
}

@media (max-width: 480px) {
  .blockly-workspace {
    height: 350px;
  }

  .progress-steps {
    display: none;
  }
}
</style>
